import type { ElementType, FunctionComponent } from 'react'
import { useMemo } from 'react'
import styled, { css } from 'styled-components'
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js'
import MAP_CONFIG from '../../legacy/services/map.config'
import type { ExtendedMapGroup } from '../../../../shared/utils/map'
import { isAuthorised } from '../../legacy/utils/map-layer'
import LayerLegendZoomButton from './LayerLegendZoomButton'
import type AuthScope from '../../../../shared/utils/api/authScope'
import RestrictedLayerCollectionAlert from './RestrictedLayerCollectionAlert'
import { getAccessToken, isAuthenticated } from '../../../../shared/utils/auth/auth'
import { createUrlWithToken } from '../../../../shared/utils/api/api'
import Label, { Position } from '../../../../shared/components/Label/Label'
import Checkbox from '../../../../shared/components/Checkbox/Checkbox'

const RestrictedLayerStyle = styled.div`
  border-bottom: 1px solid var(--greyMedium);

  img {
    display: none;
  }

  @media screen and (max-width: 48rem) {
    border-bottom: none;
  }
`

const MapLayerWithLegendStyle = styled.li<{
  notSelectable: boolean
  disabled?: boolean
  displayRestricted?: boolean
}>`
  font-size: 1rem;
  align-items: center;
  display: flex;
  padding: 0 0.75rem 0 0.5rem;
  ${({ notSelectable }) =>
    notSelectable
      ? css`
          margin-left: 23px;
        `
      : css`
          border-bottom: 1px solid var(--greyMedium);
        `}
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.9;

      img {
        opacity: 0.4;
      }
    `}
  ${({ displayRestricted }) =>
    displayRestricted &&
    css`
      border-bottom: 0;
    `}

  @media screen and (max-width: 48rem) {
    padding: 0;
    margin-top: 0.5rem;
    margin-left: 0;
    border-bottom: none;
    label span {
      font-weight: 400;
    }
  }
`

const StyledLabel = styled(Label)<{ disabled?: boolean }>`
  width: 100%;
  margin-right: auto !important;
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}
`

const StyledCheckbox = styled(Checkbox)<{ checked: boolean }>`
  padding-right: 12px;
  margin-left: -0.25rem;

  @media screen and (max-width: 48rem) {
    &:hover {
      span {
        color: var(--blue);
      }
    }
    span {
      background-color: ${({ checked }) => (checked ? 'var(--blue)' : '')};
      border-color: var(--blue);
      border-width: 2px;
    }
  }
`

const NonSelectableLegendParagraph = styled.p<{ disabled?: boolean }>`
  margin-bottom: 0.5rem;
  margin-right: auto !important;
  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
    `}
`

const MapLegendImage = styled.div`
  box-sizing: content-box;
  display: inline-block;
  height: 16px;
  line-height: 16px;
  width: 16px;
  margin-left: 0.75rem;

  & + * {
    margin-left: 0.5rem !important;
  }

  & > img {
    height: 100%;
    width: inherit;
  }

  @media screen and (max-width: 48rem) {
    height: 1.3125rem;
    width: 1.5rem;
  }
`

interface MapLayerWithLegendItemProps {
  displayRestrictedNotice?: boolean
  layerIsRestricted?: boolean
  legendItem: Pick<
    ExtendedMapGroup,
    | 'id'
    | 'isVisible'
    | 'title'
    | 'legendIconURI'
    | 'notSelectable'
    | 'iconUrl'
    | 'minZoom'
    | 'authScope'
  >
  onAddLayers: (id: string[]) => void
  onRemoveLayers: (id: string[]) => void
}

const LayerLegend: FunctionComponent<MapLayerWithLegendItemProps & Partial<HTMLLIElement>> = ({
  displayRestrictedNotice,
  layerIsRestricted,
  legendItem,
  onAddLayers,
  onRemoveLayers,
  className,
}) => {
  const appInsights = useAppInsightsContext()
  const trackEvent = useTrackEvent(appInsights, 'ENABLE_MAP_LAYER', {})
  const LegendLabel = useMemo(
    () =>
      !legendItem.notSelectable
        ? (StyledLabel as ElementType)
        : (NonSelectableLegendParagraph as ElementType),
    [legendItem],
  )

  const icon = useMemo(() => {
    // iconUrl only exists on historische kaarten + some hoogte layers
    if (legendItem.iconUrl) {
      return legendItem.iconUrl
    }

    if (isAuthenticated() && legendItem.authScope) {
      return createUrlWithToken(
        `${MAP_CONFIG.OVERLAY_ROOT}${legendItem.legendIconURI as string}`,
        getAccessToken(),
      )
    }

    return new URL(`${MAP_CONFIG.OVERLAY_ROOT}${legendItem.legendIconURI as string}`).toString()
  }, [legendItem])

  if (layerIsRestricted) {
    return (
      <RestrictedLayerStyle>
        <MapLayerWithLegendStyle
          displayRestricted={displayRestrictedNotice}
          notSelectable={legendItem.notSelectable}
          className={className}
          disabled
          data-testid={`mapLayerLegend${legendItem.title}`}
        >
          <LegendLabel
            htmlFor={legendItem.id}
            label={legendItem.title}
            disabled
            position={Position.Left}
          >
            {!legendItem.notSelectable ? (
              <StyledCheckbox
                id={legendItem.id ?? '1'}
                className="checkbox"
                // @ts-ignore
                variant="tertiary"
                checked={legendItem.isVisible ?? false}
                name={legendItem.title}
                disabled
              />
            ) : (
              legendItem.title
            )}
          </LegendLabel>
          <MapLegendImage>
            <img alt={legendItem.title} src={icon} />
          </MapLegendImage>
        </MapLayerWithLegendStyle>
        {displayRestrictedNotice && <RestrictedLayerCollectionAlert />}
      </RestrictedLayerStyle>
    )
  }

  return (
    <MapLayerWithLegendStyle
      notSelectable={legendItem.notSelectable}
      className={className}
      data-testid={`mapLayerLegend${legendItem.title}`}
    >
      <LegendLabel htmlFor={legendItem.id} label={legendItem.title} position={Position.Left}>
        {!legendItem.notSelectable ? (
          <StyledCheckbox
            id={legendItem.id ?? '1'}
            className="checkbox"
            // @ts-ignore
            variant="tertiary"
            checked={legendItem.isVisible ?? false}
            name={legendItem.title}
            onChange={() => {
              if (!legendItem.isVisible) {
                if (isAuthenticated()) {
                  trackEvent({
                    category: 'legend',
                    layer: legendItem.id,
                  })
                }
                onAddLayers([legendItem.id ?? '1'])
              } else {
                onRemoveLayers([legendItem.id ?? '1'])
              }
            }}
          />
        ) : (
          legendItem.title
        )}
      </LegendLabel>
      {isAuthorised(legendItem.authScope as AuthScope) && legendItem.isVisible && (
        <LayerLegendZoomButton minZoom={legendItem.minZoom} />
      )}
      <MapLegendImage>
        <img alt={legendItem.title} src={icon} />
      </MapLegendImage>
    </MapLayerWithLegendStyle>
  )
}

export default LayerLegend
